/**
 * Platform Admin Tools API Service
 *
 * API calls for platform-wide tool management.
 * Used by super_admin users to manage the public tool catalog.
 */

import { apiGet, apiPost, apiPatch, apiDelete } from "./api";
import type {
  PlatformTool,
  PlatformToolListResponse,
  PlatformToolStats,
  PlatformToolFilters,
  PlatformToolCreate,
  PlatformToolUpdate,
  ToolFeatureToggle,
  ToolCategory,
  ToolCategoryCreate,
  ApiPlatformTool,
  ApiPlatformToolListResponse,
  ApiPlatformToolStats,
  ApiToolCategory,
  LibraryDifficulty,
} from "@/types/adminTools";

// =============================================================================
// Response Transformers
// =============================================================================

function transformPlatformTool(tool: ApiPlatformTool): PlatformTool {
  return {
    id: tool.id,
    title: tool.title,
    description: tool.description,
    externalUrl: tool.external_url,
    topics: tool.topics,
    difficulty: tool.difficulty as LibraryDifficulty,
    author: tool.author,
    featured: tool.featured,
    thumbnailUrl: tool.thumbnail_url,
    tenantId: tool.tenant_id,
    createdAt: tool.created_at,
    updatedAt: tool.updated_at,
    totalBookmarks: tool.total_bookmarks,
    tenantCount: tool.tenant_count,
    hiddenByTenants: tool.hidden_by_tenants,
  };
}

function transformPlatformToolList(
  response: ApiPlatformToolListResponse,
): PlatformToolListResponse {
  return {
    tools: response.tools.map(transformPlatformTool),
    total: response.total,
    page: response.page,
    pageSize: response.page_size,
    totalPages: response.total_pages,
  };
}

function transformPlatformToolStats(
  stats: ApiPlatformToolStats,
): PlatformToolStats {
  return {
    totalTools: stats.total_tools,
    publicTools: stats.public_tools,
    privateTools: stats.private_tools,
    featuredTools: stats.featured_tools,
    totalBookmarks: stats.total_bookmarks,
    activeTenants: stats.active_tenants,
  };
}

function transformToolCategory(category: ApiToolCategory): ToolCategory {
  return {
    slug: category.slug,
    label: category.label,
    description: category.description,
    toolCount: category.tool_count,
  };
}

// =============================================================================
// API Calls
// =============================================================================

/**
 * List all tools with platform-wide stats
 */
export async function getPlatformAdminTools(
  filters?: PlatformToolFilters,
): Promise<PlatformToolListResponse> {
  const params: Record<string, unknown> = {};

  if (filters?.search) params.search = filters.search;
  if (filters?.toolType) params.tool_type = filters.toolType;
  if (filters?.featured !== undefined) params.featured = filters.featured;
  if (filters?.topic) params.topic = filters.topic;
  if (filters?.page) params.page = filters.page;
  if (filters?.pageSize) params.page_size = filters.pageSize;

  const response = await apiGet<ApiPlatformToolListResponse>(
    "/platform-admin/tools",
    params,
  );
  return transformPlatformToolList(response);
}

/**
 * Get platform-wide tool statistics
 */
export async function getPlatformAdminToolStats(): Promise<PlatformToolStats> {
  const response = await apiGet<ApiPlatformToolStats>(
    "/platform-admin/tools/stats",
  );
  return transformPlatformToolStats(response);
}

/**
 * Get a specific tool with platform-wide stats
 */
export async function getPlatformAdminTool(
  toolId: string,
): Promise<PlatformTool> {
  const response = await apiGet<ApiPlatformTool>(
    `/platform-admin/tools/${toolId}`,
  );
  return transformPlatformTool(response);
}

/**
 * Create a new public (platform-wide) tool
 */
export async function createPlatformTool(
  data: PlatformToolCreate,
): Promise<PlatformTool> {
  const response = await apiPost<ApiPlatformTool>("/platform-admin/tools", {
    title: data.title,
    description: data.description,
    external_url: data.externalUrl,
    topics: data.topics,
    difficulty: data.difficulty,
    author: data.author,
    thumbnail_url: data.thumbnailUrl,
    featured: data.featured,
  });
  return transformPlatformTool(response);
}

/**
 * Update a tool
 */
export async function updatePlatformTool(
  toolId: string,
  data: PlatformToolUpdate,
): Promise<PlatformTool> {
  const apiData: Record<string, unknown> = {};

  if (data.title !== undefined) apiData.title = data.title;
  if (data.description !== undefined) apiData.description = data.description;
  if (data.externalUrl !== undefined) apiData.external_url = data.externalUrl;
  if (data.topics !== undefined) apiData.topics = data.topics;
  if (data.difficulty !== undefined) apiData.difficulty = data.difficulty;
  if (data.author !== undefined) apiData.author = data.author;
  if (data.thumbnailUrl !== undefined)
    apiData.thumbnail_url = data.thumbnailUrl;
  if (data.featured !== undefined) apiData.featured = data.featured;

  const response = await apiPatch<ApiPlatformTool>(
    `/platform-admin/tools/${toolId}`,
    apiData,
  );
  return transformPlatformTool(response);
}

/**
 * Delete a tool
 */
export async function deletePlatformTool(toolId: string): Promise<void> {
  await apiDelete<void>(`/platform-admin/tools/${toolId}`);
}

/**
 * Toggle featured status of a tool
 */
export async function toggleToolFeatured(
  toolId: string,
  data: ToolFeatureToggle,
): Promise<PlatformTool> {
  const response = await apiPatch<ApiPlatformTool>(
    `/platform-admin/tools/${toolId}/feature`,
    data,
  );
  return transformPlatformTool(response);
}

/**
 * List all tool categories
 */
export async function getToolCategories(): Promise<ToolCategory[]> {
  const response = await apiGet<ApiToolCategory[]>(
    "/platform-admin/tools/categories",
  );
  return response.map(transformToolCategory);
}

/**
 * Create a new tool category
 */
export async function createToolCategory(
  data: ToolCategoryCreate,
): Promise<ToolCategory> {
  const response = await apiPost<ApiToolCategory>(
    "/platform-admin/tools/categories",
    data,
  );
  return transformToolCategory(response);
}
